import React from "react";
import PATHS from '../../utils/paths';
import useEbayFeeCalculator from "../../hooks/useEbayFeeCalculator.ts";
import {
  Box,
  Typography,
  Input,
  Card,
  Grid,
  Select,
  Option,
  Tooltip,
  Accordion,
  AccordionDetails,
  AccordionSummary
} from '@mui/joy';
import { Helmet } from 'react-helmet-async';
import Dashboard from '../dashboard/Dashboard.tsx';
import {
  FEE_STRUCTURES,
  EBAY_SELLER_LEVELS,
  OVERSEAS_SALES
} from '../../utils/calculator.ts';

// Types
type Country = 'US';
type Category = string;
type SellerLevel = string;

type OverseasSales = string;

interface StyledInputProps {
  startDecorator?: string;
  endDecorator?: string;
  value: string | number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  [key: string]: any;
}

interface InputRowProps {
  label: React.ReactNode;
  value?: string | number;
  color?: string;
  children: React.ReactNode;
}

interface TooltipLabelProps {
  title: string;
  children: React.ReactNode;
}

const TooltipLabel: React.FC<TooltipLabelProps> = ({ title, children, }) => (
  <Tooltip
    title={
      <Box sx={{ 
        maxWidth: '300px',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        lineHeight: 1.4
      }}>
        {title}
      </Box>
    }
    placement="right"
    sx={{ zIndex: 9999 }}
  >
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      '&:hover': {
        textDecoration: 'underline',
        cursor: 'default'
      }
    }}>
      {children}
    </Box>
  </Tooltip>
);

const StyledInput: React.FC<StyledInputProps> = ({
  startDecorator,
  endDecorator,
  value,
  onChange,
  placeholder,
  ...props
}) => (
  <Input
    variant="soft"
    size="lg"
    startDecorator={startDecorator}
    endDecorator={endDecorator}
    value={value}
    onChange={onChange}
    placeholder={placeholder || "0.00"}
    sx={{ width: '120px', '& input': { textAlign: 'right' } }}
    {...props}
  />
);

const InputRow: React.FC<InputRowProps> = ({ label, value, color, children }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography level="body-md">{label}</Typography>
    </Box>
    {children}
  </Box>
);

const DashedDivider: React.FC = () => (
  <Box sx={{
    height: '1px',
    background: 'repeating-linear-gradient(to right, #d1d5db 0px, #d1d5db 4px, transparent 4px, transparent 8px)',
    p: 0,
  }} />
);

const EbayCalculator: React.FC = () => {
  const canonicalUrl = `${window.location.origin}${PATHS.EBAY_FEE_CALCULATOR}`;
  const {
    cost,
    setCost,
    salePrice,
    setSalePrice,
    salesTax,
    setSalesTax,
    salesTaxAmount,
    totalEbayFee,
    finalValueFee,
    insertionFee,
    grossProfit,
    profitMargin,
    country,
    category,
    setCategory,
    shippingCharges,
    setShippingCharges,
    shippingCost,
    setShippingCost,
    quantity,
    setQuantity,
    handleQuantityBlur,
    sellerLevel,
    setSellerLevel,

    overseasSales,
    setOverseasSales,
    sellerLevelAdjustment,
    internationalFee,
  } = useEbayFeeCalculator('US');

  const EBAY_CATEGORIES = Object.keys(FEE_STRUCTURES[country as Country]);

  const colors = {
    netProfit: '#2196f3',    // blue
    cost: '#f44336',         // red
    shippingCost: '#3f51b5', // indigo
    salesTax: '#ffc107',     // amber
    fees: '#9c27b0',         // purple
  };

  const renderPriceInputs = () => (
    <>
      <Typography level="body-lg" sx={{ fontWeight: 'bold' }}>
        Listing Details
      </Typography>
      <InputRow label={
        <TooltipLabel
          title="The price you're selling your item for on eBay"
        >
          Sale Price
        </TooltipLabel>
      }>
        <StyledInput
          startDecorator="$"
          value={salePrice}
          onChange={(e) => {
            const value = e.target.value;
            setSalePrice(value);
          }}
        />
      </InputRow>
      <InputRow label={
        <TooltipLabel
          title="Number of items you're selling in this listing"
        >
          Quantity
        </TooltipLabel>
      }>
        <StyledInput
          placeholder="1"
          value={quantity}
          onChange={(e) => {
            const value = e.target.value;
            setQuantity(value);
          }}
          onBlur={handleQuantityBlur}
        />
      </InputRow>
      <InputRow label={
        <TooltipLabel
          title="How much you paid for the item (your cost basis)"
        >
          Cost of Goods
        </TooltipLabel>
      }>
        <StyledInput
          startDecorator="$"
          value={cost}
          onChange={(e) => {
            const value = e.target.value;
            setCost(value);
          }}
        />
      </InputRow>
      <InputRow label={
        <Tooltip
          title={
            <Box sx={{ 
              maxWidth: '300px',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              lineHeight: 1.4
            }}>
              Select the category that best matches your item. Different categories have different fee structures.
            </Box>
          }
          placement="right"
          sx={{ zIndex: 9999 }}
        >
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            '&:hover': {
              textDecoration: 'underline',
              cursor: 'default'
            }
          }}>
            Item Category
          </Box>
        </Tooltip>
      }>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
          <Select
            value={category}
            variant="soft"
            onChange={(event, newValue) => {
              setCategory(newValue as Category);
            }}
          >
            {EBAY_CATEGORIES.map((cat) => (
              <Option key={cat} value={cat}>
                {cat}
              </Option>
            ))}
          </Select>

          {/* Category Fee Impact Text */}
          {parseFloat(salePrice) > 0 && (
            <Typography level="body-sm" sx={{
              pt: 1,
              color: 'neutral',
              textAlign: 'right'
            }}>
              {FEE_STRUCTURES[country as Country][category]?.finalValueFee(salePrice) ?
                `${(FEE_STRUCTURES[country as Country][category].finalValueFee(salePrice) / parseFloat(salePrice) * 100).toFixed(2)}%` :
                'Standard rate'} final value fee
            </Typography>
          )}
        </Box>
      </InputRow>
    </>
  );

  const renderShippingSection = () => (
    <Accordion defaultExpanded={false}>
      <AccordionSummary
        sx={{
          '&:hover': {
            backgroundColor: 'transparent',
            color: 'inherit'
          },
          '&.Mui-expanded': {
            backgroundColor: 'transparent',
            color: 'inherit'
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            '&:hover': {
              backgroundColor: 'transparent',
              color: 'inherit'
            }
          }
        }}
      >
        <Typography level="body-lg" sx={{ fontWeight: 'bold' }}>
          Shipping
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <InputRow label={
            <TooltipLabel
              title="Shipping charges you're charging the buyer"
            >
              Shipping Charges
            </TooltipLabel>
          }>
            <StyledInput
              startDecorator="$"
              value={shippingCharges}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d*\.?\d{0,2}$/.test(value) || value === '') {
                  setShippingCharges(value);
                }
              }}
            />
          </InputRow>
          <InputRow label={
            <TooltipLabel
              title="Your actual cost to ship the item (packaging, postage, etc.)"
            >
              Shipping Cost
            </TooltipLabel>
          }>
            <StyledInput
              startDecorator="$"
              value={shippingCost}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d*\.?\d{0,2}$/.test(value) || value === '') {
                  setShippingCost(value);
                }
              }}
            />
          </InputRow>
          <InputRow label={
            <TooltipLabel
              title="Select whether you're selling to domestic or international buyers. International sales may have different fee structures."
            >
              International Sales
            </TooltipLabel>
          }>
            <Select
              value={overseasSales}
              variant="soft"
              onChange={(event, newValue) => {
                setOverseasSales(newValue as OverseasSales);
              }}
            >
              {OVERSEAS_SALES[country as Country].map((selection) => (
                <Option key={selection} value={selection}>
                  {selection}
                </Option>
              ))}
            </Select>
          </InputRow>

          {/* International fee info */}
          {overseasSales === 'Yes' && (
            <Typography level="body-sm" sx={{
              color: 'warning.600',
              textAlign: 'right',
              fontStyle: 'italic'
            }}>
              ⚠️ 1.65% international fee applies
            </Typography>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );

  const renderTaxes = () => (
    <Accordion defaultExpanded={false}>
      <AccordionSummary
        sx={{
          '&:hover': {
            backgroundColor: 'transparent',
            color: 'inherit'
          },
          '&.Mui-expanded': {
            backgroundColor: 'transparent',
            color: 'inherit'
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            '&:hover': {
              backgroundColor: 'transparent',
              color: 'inherit'
            }
          }
        }}
      >
        <Typography level="body-lg" sx={{ fontWeight: 'bold' }}>
          Taxes
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <InputRow label={
            <TooltipLabel
              title="The sales tax rate that applies to your sale. This is typically based on the buyer's location."
            >
              Sales Tax Rate
            </TooltipLabel>
          }>
            <StyledInput
              endDecorator="%"
              value={salesTax}
              onChange={(e) => {
                const value = e.target.value;
                setSalesTax(value);
              }}
            />
          </InputRow>

          <InputRow label={
            <TooltipLabel
              title="The calculated sales tax amount based on your sale price and tax rate"
            >
              Sales Tax Amount
            </TooltipLabel>
          }>
            <Typography level="body-md">
              ${Math.abs(salesTaxAmount).toFixed(2)}
            </Typography>
          </InputRow>
        </Box>
      </AccordionDetails>
    </Accordion>
  );

  const renderFees = () => (
    <>
      <Typography level="body-lg" sx={{ fontWeight: 'bold' }}>
        eBay Fees
      </Typography>

      <InputRow label={
        <TooltipLabel
          title="Fee charged by eBay when you create a listing. First 250 listings per month are free for most sellers."
        >
          Insertion Fee
        </TooltipLabel>
      }>
        <Typography level="body-md">
          ${Math.abs(insertionFee).toFixed(2)}
        </Typography>
      </InputRow>

      <InputRow label={
        <TooltipLabel
          title="Fee charged by eBay when your item sells. This is a percentage of the total sale amount plus a per-order fee."
        >
          Final Value Fee
        </TooltipLabel>
      }>
        <Typography level="body-md">
          ${Math.abs(finalValueFee).toFixed(2)}
        </Typography>
      </InputRow>

      {sellerLevelAdjustment > 0 && (
        <InputRow label={
          <TooltipLabel
            title="Additional 6% fee applied to Below Standard sellers on final value fees"
          >
            Seller Level Adjustment
          </TooltipLabel>
        }>
          <Typography level="body-md">
            ${sellerLevelAdjustment.toFixed(2)}
          </Typography>
        </InputRow>
      )}

      {internationalFee > 0 && (
        <InputRow label={
          <TooltipLabel
            title="International fee charged by eBay for international sales. This is a percentage of the total sale amount."
          >
            International Fee
          </TooltipLabel>
        }>
          <Typography level="body-md">
            ${Math.abs(internationalFee).toFixed(2)}
          </Typography>
        </InputRow>
      )}

      <InputRow label={
        <TooltipLabel
          title="Total of all eBay fees including insertion fee, final value fee, and any seller level adjustments"
        >
          Total eBay Fee
        </TooltipLabel>
      }>
        <Typography level="body-md">
          ${Math.abs(totalEbayFee).toFixed(2)}
        </Typography>
      </InputRow>
    </>
  );

  const renderStoreSettings = () => (
    <Accordion defaultExpanded={false}>
      <AccordionSummary
        sx={{
          '&:hover': {
            backgroundColor: 'transparent',
            color: 'inherit'
          },
          '&.Mui-expanded': {
            backgroundColor: 'transparent',
            color: 'inherit'
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            '&:hover': {
              backgroundColor: 'transparent',
              color: 'inherit'
            }
          }
        }}
      >
        <Typography level="body-lg" sx={{ fontWeight: 'bold' }}>
          Seller Settings
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <InputRow label={
            <TooltipLabel
              title="Your seller performance level. Below Standard sellers pay an additional 6% fee."
            >
              Seller Level
            </TooltipLabel>
          }>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
              <Select
                value={sellerLevel}
                variant="soft"
                onChange={(event, newValue) => {
                  setSellerLevel(newValue as SellerLevel);
                }}
              >
                {EBAY_SELLER_LEVELS.map((level) => (
                  <Option key={level} value={level}>
                    {level}
                  </Option>
                ))}
              </Select>

              {/* Seller Level Impact Text */}
              {parseFloat(salePrice) > 0 && (
                <Typography level="body-sm" sx={{
                  pt: 1,
                  color: sellerLevel === 'Below Standard' ? 'warning.600' : 'success.600',
                  textAlign: 'right'
                }}>
                  {sellerLevel === 'Below Standard' ?
                    `⚠️ +6% fee ($${sellerLevelAdjustment.toFixed(2)})` :
                    `✓ Standard fees apply`
                  }
                </Typography>
              )}
            </Box>
          </InputRow>
        </Box>
      </AccordionDetails>
    </Accordion>
  );

  const renderFeesFAQSection = () => (
    <Box id="fees-faq-section" sx={{ mt: 6 }}>
      <Typography level="h3" sx={{
        fontSize: "1.5em",
        fontWeight: 'bold',
        fontFamily: "Libre Baskerville",
        mb: 3,
        textAlign: 'left'
      }}>
        What Are eBay's Fee Structures and How Do They Work?
      </Typography>

      <Box sx={{ mb: 4 }}>
        <Typography level="h4" sx={{ mb: 2, fontWeight: 'bold' }}>
          What Are eBay's Selling Fees?
        </Typography>
        <Typography level="body-md" sx={{ mb: 2 }}>
          eBay charges two main types of selling fees: an insertion fee when you create a listing, and a final value fee when your item sells.
        </Typography>
        <Typography level="body-md" sx={{ mb: 2 }}>
          The amount eBay charges depends on the item's price, the format and category you choose for your listing, any optional listing upgrade fees you add, and your seller conduct and performance.
        </Typography>
        <Typography level="body-md" sx={{ mb: 2 }}>
          Do you have an eBay Store or want to sell vehicles? Fees are different for Store subscribers and for selling vehicles.
        </Typography>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography level="h4" sx={{ mb: 2, fontWeight: 'bold' }}>
          How Do eBay Insertion Fees Work?
        </Typography>
        <Typography level="body-md" sx={{ mb: 2 }}>
          Every month, you get up to 250 zero insertion fee listings, or more if you have an eBay Store. After you've used your zero insertion fee allowance, insertion fees are:
        </Typography>
        <Box sx={{ pl: 2, mb: 2 }}>
          <Typography level="body-md" sx={{ mb: 1 }}>
            • Non-refundable if your item doesn't sell
          </Typography>
          <Typography level="body-md" sx={{ mb: 1 }}>
            • Charged per listing and per category (if listing in two categories)
          </Typography>
          <Typography level="body-md" sx={{ mb: 1 }}>
            • Charged for the original listing and each time you relist
          </Typography>
          <Typography level="body-md" sx={{ mb: 1 }}>
            • Charged per listing for duplicate auction-style listings
          </Typography>
          <Typography level="body-md" sx={{ mb: 1 }}>
            • Charged only once per listing for listings with multiple items
          </Typography>
        </Box>
        <Typography level="body-md" sx={{ mb: 2 }}>
          <strong>Good 'Til Cancelled listings:</strong> These renew automatically once per calendar month. eBay charges an insertion fee and applicable optional listing upgrade fees when you list your item for the first time, and each time it renews.
        </Typography>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography level="h4" sx={{ mb: 2, fontWeight: 'bold' }}>
          What Are eBay's Final Value Fees?
        </Typography>
        <Typography level="body-md" sx={{ mb: 2 }}>
          eBay charges one final value fee when your item sells, and you don't have to worry about third-party payment processing fees. This fee is calculated as a percentage of the total amount of the sale, plus a per order fee.
        </Typography>
        <Typography level="body-md" sx={{ mb: 2 }}>
          <strong>Per Order Fee:</strong> For orders $10.00 or less the per order fee is $0.30, for orders over $10.00 the per order fee is $0.40.
        </Typography>
        <Typography level="body-md" sx={{ mb: 2 }}>
          The total amount of the sale includes the item price, any handling charges, any shipping costs collected from the buyer, sales tax, and any other applicable fees.
        </Typography>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography level="h4" sx={{ mb: 2, fontWeight: 'bold' }}>
          What Are the Basic Fees for Most eBay Categories?
        </Typography>
        <Box sx={{ overflow: 'auto' }}>
          <Box sx={{
            display: 'table',
            width: '100%',
            borderCollapse: 'collapse',
            mb: 3
          }}>
            <Box sx={{
              display: 'table-header-group',
              bgcolor: 'background.level1'
            }}>
              <Box sx={{
                display: 'table-row',
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  fontWeight: 'bold',
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  Category
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  fontWeight: 'bold',
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  Insertion Fee
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  fontWeight: 'bold'
                }}>
                  Final Value Fee
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'table-row-group' }}>
              <Box sx={{
                display: 'table-row',
                '&:hover': { bgcolor: 'background.level1' },
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  fontWeight: 'md',
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  Most categories (including eBay Motors Parts and Accessories)
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  First 250 free/month, then $0.35
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2
                }}>
                  13.6% up to $7,500, then 2.35%
                </Box>
              </Box>
              <Box sx={{
                display: 'table-row',
                '&:hover': { bgcolor: 'background.level1' },
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  fontWeight: 'md',
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  Books & Magazines, Movies & TV, Music
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  First 250 free/month, then $0.35
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2
                }}>
                  15.3% up to $7,500, then 2.35%
                </Box>
              </Box>
              <Box sx={{
                display: 'table-row',
                '&:hover': { bgcolor: 'background.level1' },
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  fontWeight: 'md',
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  Coins & Paper Money (except Bullion)
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  First 250 free/month, then $0.35
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2
                }}>
                  13.25% up to $7,500, then 2.35%
                </Box>
              </Box>
              <Box sx={{
                display: 'table-row',
                '&:hover': { bgcolor: 'background.level1' },
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  fontWeight: 'md',
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  Coins and Paper Money Bullion
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  First 250 free/month, then $0.35
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2
                }}>
                  13.6% if $7,500 or less, 7% if over $7,500
                </Box>
              </Box>
              <Box sx={{
                display: 'table-row',
                '&:hover': { bgcolor: 'background.level1' },
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  fontWeight: 'md',
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  Women's Bags & Handbags
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  First 250 free/month, then $0.35
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2
                }}>
                  15% if $2,000 or less, 9% if over $2,000
                </Box>
              </Box>
              <Box sx={{
                display: 'table-row',
                '&:hover': { bgcolor: 'background.level1' },
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  fontWeight: 'md',
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  Select Collectibles (Comic Books, Trading Cards, etc.)
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  First 250 free/month, then $0.35
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2
                }}>
                  13.25% up to $7,500, then 2.35%
                </Box>
              </Box>
              <Box sx={{
                display: 'table-row',
                '&:hover': { bgcolor: 'background.level1' },
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  fontWeight: 'md',
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  Jewelry & Watches (except Watches, Parts & Accessories)
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  First 250 free/month, then $0.35
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2
                }}>
                  15% if $5,000 or less, 9% if over $5,000
                </Box>
              </Box>
              <Box sx={{
                display: 'table-row',
                '&:hover': { bgcolor: 'background.level1' },
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  fontWeight: 'md',
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  Watches, Parts & Accessories
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  First 250 free/month, then $0.35
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2
                }}>
                  15% up to $1,000, 6.5% up to $7,500, 3% over $7,500
                </Box>
              </Box>
              <Box sx={{
                display: 'table-row',
                '&:hover': { bgcolor: 'background.level1' },
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  fontWeight: 'md',
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  NFT Categories (Art, CCG, Emerging, Movie, Music, Trading Cards)
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  First 250 free/month, then $0.35
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2
                }}>
                  5% flat rate
                </Box>
              </Box>
              <Box sx={{
                display: 'table-row',
                '&:hover': { bgcolor: 'background.level1' },
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  fontWeight: 'md',
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  Select Business & Industrial (Heavy Equipment, Printing Presses, Food Trucks)
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  $20
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2
                }}>
                  3% up to $15,000, 0.5% over $15,000
                </Box>
              </Box>
              <Box sx={{
                display: 'table-row',
                '&:hover': { bgcolor: 'background.level1' },
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  fontWeight: 'md',
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  Guitars & Basses
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  Free
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2
                }}>
                  6.7% up to $7,500, then 2.35%
                </Box>
              </Box>
              <Box sx={{
                display: 'table-row',
                '&:hover': { bgcolor: 'background.level1' },
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  fontWeight: 'md',
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  Athletic Shoes (Men's & Women's)
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  Free if starting price $150 or more
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2
                }}>
                  8% if $150 or more, 13.6% if less than $150
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography level="h4" sx={{ mb: 2, fontWeight: 'bold' }}>
          How Much Do Classified Ad Listings Cost on eBay?
        </Typography>
        <Typography level="body-md" sx={{ mb: 2 }}>
          Insertion fees for Classified Ads are $9.95 for a 30-day listing and there are no final value fees.
        </Typography>
        <Typography level="body-md" sx={{ mb: 2 }}>
          Available in: Business & Industrial, Building Materials, Office, Websites & Businesses for Sale, Specialty Services, Travel, Everything Else, eBay User Tools, Funeral & Cemetery, Information Products, Reward Points and Incentives Programs.
        </Typography>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography level="h4" sx={{ mb: 2, fontWeight: 'bold' }}>
          What Are eBay's Real Estate Listing Fees?
        </Typography>
        <Typography level="body-md" sx={{ mb: 2 }}>
          Real Estate listings include an insertion fee, a notice fee, and fees for any optional listing upgrades you choose.
        </Typography>
        <Box sx={{ overflow: 'auto' }}>
          <Box sx={{
            display: 'table',
            width: '100%',
            borderCollapse: 'collapse',
            mb: 3
          }}>
            <Box sx={{
              display: 'table-header-group',
              bgcolor: 'background.level1'
            }}>
              <Box sx={{
                display: 'table-row',
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  fontWeight: 'bold',
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  Property Type
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  fontWeight: 'bold',
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  Listing Type
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  fontWeight: 'bold',
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  Duration
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  fontWeight: 'bold',
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  Insertion Fee
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  fontWeight: 'bold'
                }}>
                  Notice Fee
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'table-row-group' }}>
              <Box sx={{
                display: 'table-row',
                '&:hover': { bgcolor: 'background.level1' },
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  fontWeight: 'md',
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  Land, Manufactured, and Timeshare homes
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  Auction-style
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  1, 3, 5, 7, or 10-day
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  $35
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2
                }}>
                  $35
                </Box>
              </Box>
              <Box sx={{
                display: 'table-row',
                '&:hover': { bgcolor: 'background.level1' },
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  fontWeight: 'md',
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  Land, Manufactured, and Timeshare homes
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  Auction-style or Fixed price
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  30-day or Good 'Til Cancelled
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  $50
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2
                }}>
                  $35
                </Box>
              </Box>
              <Box sx={{
                display: 'table-row',
                '&:hover': { bgcolor: 'background.level1' },
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  fontWeight: 'md',
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  Land, Manufactured, and Timeshare homes
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  Classified Ad
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  30-day
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  $150
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2
                }}>
                  $0
                </Box>
              </Box>
              <Box sx={{
                display: 'table-row',
                '&:hover': { bgcolor: 'background.level1' },
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  fontWeight: 'md',
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  Commercial, Residential, and Other Real Estate
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  Auction-style
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  1, 3, 5, 7, or 10-day
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  $100
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2
                }}>
                  $0
                </Box>
              </Box>
              <Box sx={{
                display: 'table-row',
                '&:hover': { bgcolor: 'background.level1' },
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  fontWeight: 'md',
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  Commercial, Residential, and Other Real Estate
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  Auction-style or Fixed price
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  30-day or Good 'Til Cancelled
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2,
                  borderRight: '1px solid',
                  borderColor: 'divider'
                }}>
                  $150
                </Box>
                <Box sx={{
                  display: 'table-cell',
                  p: 2
                }}>
                  $0
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        <Typography level="body-md" sx={{ mb: 2 }}>
          <strong>Note:</strong> Notice fees are only charged when there are bids or when reserve prices are met. No notice fee for Classified Ad format.
        </Typography>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography level="h4" sx={{ mb: 2, fontWeight: 'bold' }}>
          What Optional Listing Upgrades Does eBay Offer and How Much Do They Cost?
        </Typography>
        <Typography level="body-md" sx={{ mb: 2 }}>
          You can enhance your listings with optional features. Fees vary depending on your item's price and the listing format and duration.
        </Typography>
        <Typography level="body-md" sx={{ mb: 2 }}>
          <strong>Free features:</strong> Up to 24 photos with zoom and enlarge, schedule listings (except Classified Ads which cost $0.10).
        </Typography>

        <Typography level="title-lg" sx={{ mb: 2, fontWeight: 'bold', mt: 3 }}>
          Auction-style Listings (1, 3, 5, 7, 10-day duration)
        </Typography>
        <Box sx={{ pl: 2, mb: 2 }}>
          <Typography level="body-md" sx={{ mb: 1 }}>
            • <strong>Bold:</strong> $2.00
          </Typography>
          <Typography level="body-md" sx={{ mb: 1 }}>
            • <strong>Gallery Plus:</strong> $0.35 (Free for Art, Antiques, Collectibles, Pottery & Glass)
          </Typography>
          <Typography level="body-md" sx={{ mb: 1 }}>
            • <strong>List in two categories:</strong> Insertion fee for second category + upgrade fees
          </Typography>
          <Typography level="body-md" sx={{ mb: 1 }}>
            • <strong>Subtitle:</strong> $1.50 ($150 or less), $3.00 (over $150), $0.50 (Real Estate)
          </Typography>
          <Typography level="body-md" sx={{ mb: 1 }}>
            • <strong>Reserve price:</strong> $5.00 or 7.5% of reserve price, whichever is greater (max $250)
          </Typography>
          <Typography level="body-md" sx={{ mb: 1 }}>
            • <strong>International site visibility:</strong> $0.10 ($9.99 or less), $0.20 ($10-$49.99), $0.40 ($50 or more)
          </Typography>
        </Box>

        <Typography level="title-lg" sx={{ mb: 2, fontWeight: 'bold', mt: 3 }}>
          Fixed Price Listings (Good 'Til Cancelled)
        </Typography>
        <Box sx={{ pl: 2, mb: 2 }}>
          <Typography level="body-md" sx={{ mb: 1 }}>
            • <strong>Bold:</strong> $4.00
          </Typography>
          <Typography level="body-md" sx={{ mb: 1 }}>
            • <strong>Gallery Plus:</strong> $1.00 (Free for Art, Antiques, Collectibles, Pottery & Glass)
          </Typography>
          <Typography level="body-md" sx={{ mb: 1 }}>
            • <strong>International site visibility:</strong> $0.50
          </Typography>
          <Typography level="body-md" sx={{ mb: 1 }}>
            • <strong>List in two categories:</strong> Insertion fee for second category + upgrade fees
          </Typography>
          <Typography level="body-md" sx={{ mb: 1 }}>
            • <strong>Scheduled Listing:</strong> Free
          </Typography>
          <Typography level="body-md" sx={{ mb: 1 }}>
            • <strong>Subtitle:</strong> $2.00 ($150 or less), $6.00 (over $150), $1.50 (Classified Ads and Real Estate)
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography level="h4" sx={{ mb: 2, fontWeight: 'bold' }}>
          When Do Additional Final Value Fees Apply on eBay?
        </Typography>
        <Typography level="body-md" sx={{ mb: 2 }}>
          Additional fees may apply in the following circumstances:
        </Typography>
        <Box sx={{ pl: 2, mb: 2 }}>
          <Typography level="body-md" sx={{ mb: 1 }}>
            • <strong>Below Standard sellers:</strong> Additional 6% on final value fees for the following month
          </Typography>
          <Typography level="body-md" sx={{ mb: 1 }}>
            • <strong>High return rate:</strong> Additional 5% on final value fees for categories with "Very High" Item Not As Described return rates
          </Typography>
          <Typography level="body-md" sx={{ mb: 1 }}>
            • <strong>Buying/selling outside eBay:</strong> Final value fee based on total sale amount even if item doesn't sell
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography level="h4" sx={{ mb: 2, fontWeight: 'bold' }}>
          What Other Fees Should eBay Sellers Be Aware Of?
        </Typography>
        <Box sx={{ pl: 2, mb: 2 }}>
          <Typography level="body-md" sx={{ mb: 1 }}>
            • <strong>Dispute fee:</strong> $20.00 for each chargeback or policy violation
          </Typography>
          <Typography level="body-md" sx={{ mb: 1 }}>
            • <strong>International fee:</strong> 1.65% of total sale amount for international sales (unless using eBay International Shipping)
          </Typography>
          <Typography level="body-md" sx={{ mb: 1 }}>
            • <strong>Seller currency conversion charge:</strong> 3% for US sellers when converting between currencies
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Dashboard>
      <Helmet>
        <title>eBay Fee Calculator - Calculate your net profits after all your fees and expenses</title>
        <meta name="description" content="Determine your eBay fees and potential profits after all your expenses using our free eBay calculator" />
        <meta property="og:title" content="eBay Fee Calculator" />
        <meta property="og:description" content="Calculate Your Profits Instantly" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      <Box>
        <Typography level="h2" sx={{
          fontSize: "1.8em",
          fontWeight: 'bold',
          fontFamily: "Libre Baskerville",
          pb: 1
        }}>
          eBay Fee Calculator
        </Typography>
        <Typography level="body-md" sx={{ mb: 4, color: 'text.secondary' }}>
          Calculate your net profits after all your fees and expenses.
        </Typography>

        <Grid container spacing={3} justifyContent="left" marginTop={4}>
          <Grid xs={12} md={6}>
            <Card
              variant="outlined"
              sx={{
                p: 3,
                bgcolor: '#F9FAFB',
                border: '1px solid',
                borderColor: 'neutral.200',
                borderRadius: 16,
                height: 'fit-content'
              }}
            >
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2
              }}>
                {renderPriceInputs()}
                <DashedDivider />
                {renderShippingSection()}
                <DashedDivider />
                {renderStoreSettings()}
                <DashedDivider />
                {renderTaxes()}
              </Box>
            </Card>
          </Grid>

          <Grid xs={12} md={6}>
            <Card
              variant="outlined"
              sx={{
                p: 3,
                bgcolor: 'white',
                boxShadow: 'lg',
                borderRadius: 'xl',
                height: 'fit-content',
                position: { xs: 'static', md: 'sticky' },
                top: { md: 20 }
              }}
            >
              {renderFees()}
              <DashedDivider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography level="body-lg" fontWeight="lg">Profit Margin</Typography>
                </Box>
                <Typography
                  level="body-md"
                  fontWeight="lg"
                  sx={{ color: profitMargin < 0 ? 'danger.500' : 'success.500' }}
                >
                  {profitMargin.toFixed(1)}%
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography level="body-lg" fontWeight="lg">Net Profit</Typography>
                </Box>
                <Typography level="h2" sx={{
                  color: parseFloat(salePrice) === 0 && parseFloat(cost) === 0
                    ? 'neutral.500'
                    : grossProfit < 0
                      ? 'danger.500'
                      : 'success.500',
                }}>
                  {parseFloat(salePrice) === 0 && parseFloat(cost) === 0
                    ? '$0.00'
                    : grossProfit < 0
                      ? `-$${Math.abs(grossProfit).toFixed(2)}`
                      : `$${grossProfit.toFixed(2)}`}
                </Typography>
              </Box>
              <Box sx={{ mt: 2 }}>
                {parseFloat(salePrice) > 0 ? (
                  <>
                    <Box sx={{
                      height: 8,
                      borderRadius: 4,
                      display: 'flex',
                      overflow: 'hidden'
                    }}>
                      {parseFloat(cost) > 0 && (
                        <Box sx={{
                          width: `${(parseFloat(cost) / parseFloat(salePrice)) * 100}%`,
                          bgcolor: colors.cost
                        }} />
                      )}
                      {totalEbayFee > 0 && (
                        <Box sx={{
                          width: `${(totalEbayFee / parseFloat(salePrice)) * 100}%`,
                          bgcolor: colors.fees
                        }} />
                      )}
                      {salesTaxAmount > 0 && (
                        <Box sx={{
                          width: `${(salesTaxAmount / parseFloat(salePrice)) * 100}%`,
                          bgcolor: colors.salesTax
                        }} />
                      )}
                      {parseFloat(shippingCost) > 0 && (
                        <Box sx={{
                          width: `${(parseFloat(shippingCost) / parseFloat(salePrice)) * 100}%`,
                          bgcolor: colors.shippingCost
                        }} />
                      )}
                      {grossProfit > 0 && (
                        <Box sx={{
                          width: `${(grossProfit / parseFloat(salePrice)) * 100}%`,
                          bgcolor: colors.netProfit
                        }} />
                      )}
                    </Box>

                    {/* Legend */}
                    <Box sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 2,
                      mt: 2,
                      justifyContent: 'center'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: 2, bgcolor: colors.cost }} />
                        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>Cost</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: 2, bgcolor: colors.fees }} />
                        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>Fees</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: 2, bgcolor: colors.salesTax }} />
                        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>Tax</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: 2, bgcolor: colors.shippingCost }} />
                        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>Shipping</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: 2, bgcolor: colors.netProfit }} />
                        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>Profit</Typography>
                      </Box>
                    </Box>
                  </>
                ) : (
                  <>
                    <Box sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: 'neutral.300',
                      width: '100%'
                    }} />
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      mt: 2
                    }}>
                      <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                        Enter sale price to see breakdown
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            </Card>
          </Grid>
        </Grid>

        {renderFeesFAQSection()}
      </Box>
    </Dashboard>
  );
};

export default EbayCalculator;

